// The Refer a Company form (MOTHER-88): a verified partner introduces a company, with the
// ten REFERRAL_RULES fields and a confirmation that the person knows. Props only, so tests
// render it with fake data; PartnersPage.tsx shows it at /refer for a verified partner.
// No terms text and no erasure or privacy copy (Cameron, 2026-10-26).
import { Button, Card, CardBody, Checkbox, Field, FormGrid, FormGridItem, InlineMessage, Input, OptionSelect, Stack, Textarea } from '@olytic/foundation';
import { type FormEvent, useState } from 'react';

import type { ReferralFormState } from './flow';
import { BackToMyLeads, LeadsFrame, type LeadPageActions } from './LeadPages';
import { CONSENT_LABEL, emptyReferral, REFERRAL_FIELDS, type ReferralKey, type ReferralValues, TIMELINE_OPTIONS } from './referral';

const RULE = Object.fromEntries(REFERRAL_FIELDS.map((rule) => [rule.key, rule])) as Record<ReferralKey, (typeof REFERRAL_FIELDS)[number]>;
const TIMELINES = TIMELINE_OPTIONS.map((option) => ({ value: option, label: option }));

export interface ReferPageProps extends LeadPageActions {
  /** After a refused submit: the values typed, and why. */
  form: ReferralFormState | undefined;
  onRefer(values: ReferralValues): void;
}

/**
 * "Refer a Company": the ten fields in the API's order, then the required consent box.
 * The submit button is disabled while the request runs (`busy`), so a double click sends
 * one request.
 */
export function ReferPage({ form, onRefer, ...actions }: ReferPageProps): JSX.Element {
  const [values, setValues] = useState<ReferralValues>(() => form?.values ?? emptyReferral());
  const fieldErrors = form?.fieldErrors ?? {};
  const set = (key: ReferralKey) => (value: string) => setValues((current) => ({ ...current, [key]: value }));
  const field = (key: ReferralKey) => ({ label: RULE[key].label, required: RULE[key].required, error: fieldErrors[key] });
  const input = (key: ReferralKey) => ({ name: key, value: values[key], onChange: (event: { target: { value: string } }) => set(key)(event.target.value) });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (actions.busy) return;
    onRefer(values);
  };

  return (
    <LeadsFrame title="Refer a Company" width="default" breadcrumbs={<BackToMyLeads onOpen={actions.onOpen} />} actions={actions}>
      <Card>
        <CardBody>
          <Stack as="form" gap="md" noValidate onSubmit={submit} aria-label="Refer a Company">
            {form?.error ? (
              <InlineMessage tone="error" role="alert">
                {form.error}
              </InlineMessage>
            ) : null}
            <FormGrid columns={2}>
              <Field {...field('firstName')}>
                <Input {...input('firstName')} />
              </Field>
              <Field {...field('lastName')}>
                <Input {...input('lastName')} />
              </Field>
              <Field {...field('email')}>
                <Input {...input('email')} type="email" />
              </Field>
              <Field {...field('phone')}>
                <Input {...input('phone')} type="tel" />
              </Field>
              <Field {...field('title')}>
                <Input {...input('title')} />
              </Field>
              <Field {...field('companyName')}>
                <Input {...input('companyName')} />
              </Field>
              <FormGridItem span="full">
                <Field {...field('companyWebsite')}>
                  <Input {...input('companyWebsite')} type="url" />
                </Field>
              </FormGridItem>
              <FormGridItem span="full">
                <Field {...field('relationship')}>
                  <Textarea {...input('relationship')} rows={3} />
                </Field>
              </FormGridItem>
              <FormGridItem span="full">
                <Field {...field('need')}>
                  <Textarea {...input('need')} rows={5} />
                </Field>
              </FormGridItem>
              <Field {...field('timeline')}>
                <OptionSelect name="timeline" options={TIMELINES} placeholder="Choose one" value={values.timeline} onValueChange={set('timeline')} />
              </Field>
              <FormGridItem span="full">
                <Field label={CONSENT_LABEL} required error={fieldErrors.consent}>
                  <Checkbox
                    name="consent"
                    checked={values.consent}
                    onCheckedChange={(checked) => setValues((current) => ({ ...current, consent: checked === true }))}
                  />
                </Field>
              </FormGridItem>
            </FormGrid>
            <Button type="submit" variant="brand" loading={actions.busy}>
              {form?.retry ? 'Try Again' : 'Submit Lead'}
            </Button>
          </Stack>
        </CardBody>
      </Card>
    </LeadsFrame>
  );
}
